import { useState } from 'react'

import {
  Box,
  useUnsafeCallback,
  useUnsafeEffect,
  useUnsafeMemo,
} from '@factorialco/deprecated-design-system'

import AddFilter from 'modules/core/components/Filter/filters/AddFilter'
import { useAddFilter } from 'modules/core/components/Filter/hooks'

import { useFilterProvider } from '../../Providers/FiltersProvider'
import { useFinancialWorkspace } from '../../Providers/Provider'
import { AdditionalFilters, FilterValue } from '../../types/filter'
import ClearFilter from './ClearFilter'
import { upToTwoFiltersApplied } from './helpers'
import { useDefaultFilters } from './useDefaultFilters'

interface FinancialWorkspaceFilterProps {
  additionalFilters?: () => AdditionalFilters
}

export const Filters = ({ additionalFilters }: FinancialWorkspaceFilterProps) => {
  const [recentlyAddedFilter, setRecentlyAddedFilter] = useState<string | undefined>()
  const defaultFilters = useDefaultFilters(recentlyAddedFilter)
  const { resetSavedFilters } = useFilterProvider()
  const newFilters = additionalFilters?.()
  const { state } = useFinancialWorkspace()

  const filtersToAdd = useUnsafeMemo(
    () => newFilters?.(recentlyAddedFilter),
    [newFilters, recentlyAddedFilter]
  )

  const allFilters: { [key: string]: FilterValue } = useUnsafeMemo(() => {
    let filters = {
      ...filtersToAdd,
      ...defaultFilters,
    }

    filters = Object.fromEntries(
      Object.entries(filters).filter(([, value]) => value?.visible !== false)
    )

    return Object.fromEntries(
      Object.entries(filters).sort(([, a], [, b]) => {
        return (a?.position ?? 0) - (b?.position ?? 0)
      })
    )
  }, [defaultFilters, filtersToAdd])

  const [selectedFilters, setSelectedFilters] = useState<string[]>([])

  const filterKeys = useUnsafeMemo(() => Object.values(allFilters).map((i) => i.key), [allFilters])

  const showClearFilters = useUnsafeMemo(
    () => upToTwoFiltersApplied(filterKeys, state),
    [filterKeys, state]
  )

  const onAddFilter = useUnsafeCallback(
    (filter) => {
      if (!selectedFilters.includes(filter)) {
        setRecentlyAddedFilter(filter)
        setSelectedFilters((selected) => [...selected, filter])
      }
    },
    [selectedFilters]
  )

  const { availableFilters } = useAddFilter({
    allFilters,
    selectedFilters,
    onAddFilter,
  })

  const onClearFilters = useUnsafeCallback(() => {
    resetSavedFilters()

    Object.values(allFilters).forEach((filter) => {
      if (filter.onReset) {
        filter.onReset()
      }
    })

    setSelectedFilters([])
  }, [allFilters, resetSavedFilters])

  useUnsafeEffect(() => {
    if (!selectedFilters.length) {
      setSelectedFilters(Object.keys(allFilters))
    }
  }, [selectedFilters])

  return (
    <Box flexDirection="row" gap="s8">
      {selectedFilters.map((setSelectedFilter) => allFilters[setSelectedFilter]?.component)}
      {availableFilters.length > 0 && (
        <AddFilter
          availableFilters={availableFilters}
          onSelectFilter={onAddFilter}
          color="grey800"
        />
      )}
      {showClearFilters && <ClearFilter onClearFilters={onClearFilters} />}
    </Box>
  )
}
